import { useEffect } from 'react';

/**
 * Dynamic-rendering (prerender) readiness signal.
 *
 * The edge prerender service (see nginx.conf / docker-compose) loads the SPA in
 * a headless browser and snapshots the DOM once `window.prerenderReady === true`.
 * We start as `false` (set inline in public/index.html) so the crawler waits for
 * async job/route data instead of snapshotting the empty shell, then flip to
 * `true` when the page's real content has rendered.
 *
 * Pages that fetch data should call `usePrerenderReady(!loading)` so the snapshot
 * captures fully-rendered content (title, description, JSON-LD, etc.). Purely
 * static pages don't need it — the prerender service falls back to a network-idle
 * timeout — but calling it makes the snapshot deterministic and faster.
 */

export function markPrerenderLoading() {
  if (typeof window !== 'undefined') window.prerenderReady = false;
}

export function markPrerenderReady() {
  if (typeof window !== 'undefined') window.prerenderReady = true;
}

/**
 * Flip the prerender flag based on a readiness boolean.
 * @param {boolean} isReady - true once the page's primary content has rendered.
 */
export function usePrerenderReady(isReady) {
  useEffect(() => {
    if (isReady) markPrerenderReady();
    else markPrerenderLoading();
  }, [isReady]);
}
